import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Form, Badge } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaLock, FaPlus } from 'react-icons/fa';
import CvPageLayout from '../../../components/CvPageLayout';
import CvNavigation from '../../../components/CvNavigation';
import Loader from '../../../components/Loader';
import { fetchSections, updateSection } from '../../../actions/additionalSectionsActions';

function CvGeneratorBonusInfo() {
  const [selectedSections, setSelectedSections] = useState([]);
  const [customSectionName, setCustomSectionName] = useState('');
  const [lockedSections, setLockedSections] = useState([]);

  const dispatch = useDispatch();
  const { sections = [], loading } = useSelector((state) => state.additionalSectionsList || {});

  const routesMap = {
    check_1: '/cv-career/cv-generator/section/professional-summary',
    check_2: '/cv-career/cv-generator/section/achievements',
    check_3: '/cv-career/cv-generator/section/associations',
    check_4: '/cv-career/cv-generator/section/links',
    check_5: '/cv-career/cv-generator/section/additional-info',
    check_6: '/cv-career/cv-generator/section/certificates',
    check_7: '/cv-career/cv-generator/section/custom',
  };

  const sectionItems = [
    { id: 'check_1', label: 'Podsumowanie zawodowe', icon: '📝' },
    { id: 'check_2', label: 'Osiągnięcia', icon: '🏆' },
    { id: 'check_3', label: 'Stowarzyszenia', icon: '🤝' },
    { id: 'check_4', label: 'Strony, portfolia, profile zawodowe', icon: '🔗' },
    { id: 'check_5', label: 'Dodatkowe informacje', icon: 'ℹ️' },
    { id: 'check_6', label: 'Certyfikaty', icon: '📜' },
  ];

  useEffect(() => {
    dispatch(fetchSections());
  }, [dispatch]);

  useEffect(() => {
    if (!sections) return;

    const sectionArray = sections.results || [];

    const initialSelected = [];
    const initialLocked = [];

    sectionArray.forEach((s) => {
      const hasContent =
        (Array.isArray(s.content) && s.content.length > 0) ||
        (typeof s.content === 'string' && s.content.trim() !== '') ||
        (typeof s.content === 'object' && s.content !== null && Object.keys(s.content).length > 0);

      if (hasContent) {
        const key = s.section_type;
        if (key) {
          initialSelected.push(key);
          initialLocked.push(key);
        }

        if (key === 'check_7') {
          setCustomSectionName(s.name || '');
        }
      }
    });

    setSelectedSections(initialSelected);
    setLockedSections(initialLocked);
  }, [sections]);

  const handleCheckboxChange = (id) => {
    if (lockedSections.includes(id)) return;
    
    setSelectedSections((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleCustomInputChange = (e) => {
    const value = e.target.value;
    setCustomSectionName(value);

    setSelectedSections((prev) => {
      const exists = prev.includes('check_7');
      if (value.trim() !== '' && !exists) {
        return [...prev, 'check_7'];
      } else if (value.trim() === '' && exists) {
        return prev.filter((item) => item !== 'check_7');
      }
      return prev;
    });
  };

  const getNextLink = () => {
    if (selectedSections.length > 0) {
      return routesMap[selectedSections[0]];
    } else {
      return '/cv-career/cv-generator/section/summary';
    }
  };

  const handleNext = () => {
    const trimmedCustomName = customSectionName.trim();
    localStorage.setItem('selectedSections', JSON.stringify(selectedSections));
    localStorage.setItem('customSectionName', customSectionName.trim());
    const isCustomSectionSelected = selectedSections.includes('check_7');
    const customSection = (sections.results || []).find(s => s.section_type === 'check_7');

    if (isCustomSectionSelected && trimmedCustomName !== '') {
      const id = customSection ? customSection.id : null; 
      if (id) {
        try {
          const sectionToUpdate = {
            id: id,
            section_type: 'check_7',
            name: trimmedCustomName,
            content: customSection.content || [], 
          };
          dispatch(updateSection(sectionToUpdate));
        } catch (error) {
          console.error("Błąd podczas aktualizacji nazwy sekcji niestandardowej:", error);
        }
      } 
    }
  };

  if (loading) {
    return (
      <CvPageLayout currentStep="Sekcje dodatkowe">
        <Loader />
      </CvPageLayout>
    );
  }

  return (
    <CvPageLayout currentStep="Sekcje dodatkowe">
      <Row className="justify-content-md-center mt-2">
        <Col>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="fw-bold">
              Chcesz dodać którąś z tych sekcji?
            </p>
            <p className="text-muted">Te sekcje są opcjonalne, ale mogą wzbogacić Twoje CV.</p>
          </motion.div>
        </Col>
      </Row>

      <Row className="g-3 mt-3">
        {/* STANDARDOWE SEKCJE */}
        <Col xs={12} lg={6}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card 
              className="shadow-lg border-0"
              style={{ borderRadius: '20px', overflow: 'hidden' }}
            >
              <Card.Header
                style={{
                  background: 'linear-gradient(135deg, var(--bs-primary) 0%, #0056b3 100%)',
                  border: 'none',
                  padding: '1rem 1.5rem'
                }}
              >
                <h5 className="text-white mb-0 fw-bold">
                  Standardowe sekcje
                </h5>
              </Card.Header>
              <Card.Body className="p-3">
                {sectionItems.map((item, index) => {
                  const isSelected = selectedSections.includes(item.id);
                  const isLocked = lockedSections.includes(item.id);

                  return (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Card
                        className="mb-2 border-0"
                        style={{
                          borderRadius: '12px',
                          cursor: isLocked ? 'not-allowed' : 'pointer',
                          background: isSelected 
                            ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                            : 'rgba(0, 0, 0, 0.02)',
                          border: isSelected ? '2px solid var(--bs-primary)' : '2px solid transparent',
                          transition: 'all 0.3s ease',
                          opacity: isLocked ? 0.7 : 1
                        }}
                        onClick={() => handleCheckboxChange(item.id)}
                      >
                        <Card.Body className="p-3">
                          <div className="d-flex align-items-center">
                            <div
                              className="d-flex align-items-center justify-content-center rounded-circle me-3"
                              style={{
                                width: '40px',
                                height: '40px',
                                background: isSelected 
                                  ? 'linear-gradient(135deg, var(--bs-primary) 0%, #0056b3 100%)'
                                  : 'rgba(var(--bs-primary-rgb), 0.1)',
                                transition: 'all 0.3s ease',
                                flexShrink: 0
                              }}
                            >
                              {isSelected ? (
                                <FaCheckCircle className="text-white" size={20} />
                              ) : (
                                <span style={{ fontSize: '1.2rem' }}>{item.icon}</span>
                              )}
                            </div>
                            <div className="flex-grow-1">
                              <span 
                                className="fw-medium"
                                style={{ 
                                  fontSize: '0.95rem',
                                  color: isSelected ? 'var(--bs-primary)' : 'inherit'
                                }}
                              >
                                {item.label}
                              </span>
                            </div>
                            {isLocked && (
                              <Badge 
                                bg="light" 
                                text="dark"
                                className="d-flex align-items-center"
                                style={{ fontSize: '0.75rem' }}
                              >
                                <FaLock className="me-1" size={10} />
                                Wypełniona
                              </Badge>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    </motion.div>
                  );
                })}
              </Card.Body>
            </Card>
          </motion.div>
        </Col>

        {/* WŁASNA SEKCJA */}
        <Col xs={12} lg={6}>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card 
              className="shadow-lg border-0"
              style={{ borderRadius: '20px', overflow: 'hidden' }}
            >
              <Card.Header
                style={{
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  border: 'none',
                  padding: '1rem 1.5rem'
                }}
              >
                <h5 className="text-white mb-0 fw-bold">
                  <FaPlus className="me-2"/>
                  Własna sekcja
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Form.Group>
                  <Form.Label className="fw-medium mb-3">
                    Dodaj sekcję niestandardową
                  </Form.Label>
                  <div 
                    className="p-4"
                    style={{
                      background: selectedSections.includes('check_7')
                        ? 'linear-gradient(135deg, rgba(240, 147, 251, 0.1) 0%, rgba(245, 87, 108, 0.1) 100%)'
                        : 'rgba(0, 0, 0, 0.02)',
                      borderRadius: '15px',
                      border: selectedSections.includes('check_7') 
                        ? '2px solid #f093fb' 
                        : '2px dashed #dee2e6',
                      transition: 'all 0.3s ease',                      
                    }}
                  >
                    <div className="d-flex align-items-center mb-3">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle me-3"
                        style={{
                          width: window.innerWidth >= 768 ? '40px' : '35px',
                          height: window.innerWidth >= 768 ? '40px' : '35px',
                          background: selectedSections.includes('check_7')
                            ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                            : 'rgba(240, 147, 251, 0.2)',
                          transition: 'all 0.3s ease',
                        }}
                      >
                        {selectedSections.includes('check_7') ? (
                          <FaCheckCircle className="text-white" size={16} />
                        ) : (
                          <FaPlus className="text-muted" size={16} />
                        )}
                      </div>
                      <Form.Control
                        type="text"
                        placeholder="np. Zainteresowania, Projekty..."
                        value={customSectionName}
                        onChange={handleCustomInputChange}
                        disabled={lockedSections.includes('check_7')}
                        style={{
                          borderRadius: '10px',
                          border: '2px solid #e9ecef',
                          padding: '0.75rem',
                          fontSize: '0.95rem',
                          maxWidth: '240px',
                          background: lockedSections.includes('check_7') ? '#f5f5f5' : 'white'
                        }}
                      />
                    </div>
                    {lockedSections.includes('check_7') && (
                      <Badge 
                        bg="light" 
                        text="dark"
                        className="d-flex align-items-center w-100 justify-content-center py-2"
                      >
                        <FaLock className="me-2" size={12} />
                        Ta sekcja została już wypełniona
                      </Badge>
                    )}
                    <Form.Text className="text-muted d-block mt-2">
                      <small> Wpisz nazwę sekcji, aby ją aktywować</small>
                    </Form.Text>
                  </div>
                </Form.Group>

                {/* INFO BOX */}
                <Card 
                  className="border-0 mt-4"
                  style={{
                    background: 'linear-gradient(135deg, rgba(79, 172, 254, 0.1) 0%, rgba(0, 242, 254, 0.1) 100%)',
                    borderRadius: '15px'
                  }}
                >
                  <Card.Body className="p-3">
                    <div className="text-center">
                      <small className="text-muted">
                        Własna sekcja pozwala dodać unikalne informacje, których nie ma w standardowych kategoriach
                      </small>
                    </div>
                  </Card.Body>
                </Card>
              </Card.Body>
            </Card>
          </motion.div>
        </Col>
      </Row>

      {/* PODSUMOWANIE WYBRANYCH */}
      {selectedSections.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-4"
        >
          <Card 
            className="border-0 shadow-lg"
            style={{
              borderRadius: '20px',
              background: 'linear-gradient(135deg, rgba(67, 233, 123, 0.1) 0%, rgba(56, 249, 215, 0.1) 100%)'
            }}
          >
            <Card.Body className="p-4">
              <h6 className="fw-bold mb-3">
                <FaCheckCircle className="me-2 text-success" />
                Wybrane sekcje ({selectedSections.length})
              </h6>
              <div className="d-flex flex-wrap gap-2">
                {selectedSections.map(id => {
                  const item = sectionItems.find(s => s.id === id);
                  const displayLabel = id === 'check_7' 
                    ? customSectionName || 'Własna sekcja'
                    : item?.label;
                  
                  return (
                    <Badge 
                      key={id}
                      bg="light"
                      text="dark"
                      className="px-3 py-2"
                      style={{ 
                        fontSize: '0.9rem',
                        borderRadius: '12px',
                        border: '2px solid var(--bs-primary)'
                      }}
                    >
                      {id === 'check_7' ? '✨' : (item?.icon || '📝')} {displayLabel}
                    </Badge>
                  );
                })}
              </div>
            </Card.Body>
          </Card>
        </motion.div>
      )}

      <CvNavigation
        backlink="/cv-career/cv-generator/section/languages"
        nextlink={getNextLink()}
        onNext={handleNext}
      />
    </CvPageLayout>
  );
}

export default CvGeneratorBonusInfo;